import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { ControlledTextInput } from "@/components/FormInputs";
import { CrossIcon } from "@/components/Icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { graphql } from "@/gen";
import { useImageUpload } from "@/hooks/use-image-upload";
import { cx } from "@/utils/cx";
import { generateImageUrl } from "@/utils/image";

export const ImageUploadInput = ({
    imageId,
    onImageId,
    label,
    validationError,
    className,
}: {
    imageId?: string | null;
    onImageId: (imageId: string | null) => void;
    label?: string;
    validationError?: string;
    className?: string;
}) => {
    const { startSelectFile, inputProps, resetInput, isUploading, inputId } = useImageUpload({
        onUploadFinished: onImageId,
    });

    const removeItem = () => {
        onImageId(null);
        resetInput();
    };

    return (
        <Card className="bg-brand-gray-lightbg text-brand-black rounded border-none p-4">
            <div className={cx("flex flex-col items-start gap-2", className)}>
                <div className="flex flex-col space-y-2">
                    {label ? <Label htmlFor={inputId}>{label}</Label> : null}
                    <Button
                        type={"button"}
                        variant="outline"
                        onClick={() => {
                            startSelectFile();
                        }}
                    >
                        Select file
                    </Button>
                    <Input {...inputProps} />
                    {imageId ? (
                        <div className="my-2 w-full rounded-lg bg-sky-100 px-5 py-3">
                            <div className="flex w-full items-center justify-between">
                                <div className="flex items-center gap-5">
                                    <img
                                        src={generateImageUrl({ imageId, width: 300 })}
                                        alt="avatar"
                                        className="image-input-wrapper opacity-75-hover cursor-pointer"
                                    />
                                </div>
                                <span onClick={() => removeItem()} className="cursor-pointer">
                                    <CrossIcon />
                                </span>
                            </div>
                        </div>
                    ) : null}

                    <ImageCreditForm imageId={imageId!} />

                    {isUploading && (
                        <div className="flex h-48 w-1/3 items-center justify-center rounded-lg border-2 border-dashed p-4">
                            <p>Loading...</p>
                        </div>
                    )}
                </div>
                {validationError ? (
                    <div className="text-sm text-red-500">{validationError}</div>
                ) : null}
            </div>
        </Card>
    );
};
const ImageCreditForm = ({ imageId }: { imageId?: string | null }) => {
    const [credit, setCredit] = useState<string | null>(null);
    const id = useId();
    const [{ error: _updateImageCreditErr }, updateImageCredit] = useMutation(
        graphql(`
            mutation updateImageCredit($input: UpdateImageCreditInput!) {
                updateImageCredit(input: $input) {
                    ok
                }
            }
        `)
    );

    const [imageResult] = useQuery({
        query: graphql(`
            query image($id: UUID!) {
                image(id: $id) {
                    credit
                }
            }
        `),
        variables: { id: imageId! },
        pause: !imageId,
    });

    useEffect(() => {
        if (imageResult.data?.image?.credit) {
            setCredit(imageResult.data.image.credit);
        }
    }, [imageResult.data]);

    const onSubmit = async () => {
        if (!imageId) {
            return;
        }

        const result = await updateImageCredit({
            input: {
                imageId,
                credit: credit || null,
            },
        });

        if (result.error) {
            toast.error("Unable to update image credit", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Image credit was saved");
        }
    };

    if (!imageResult.data?.image) {
        return null;
    }

    return (
        <div className="w-1/2 space-y-2">
            <Label htmlFor={id}>Credit</Label>
            <ControlledTextInput
                value={credit ?? ""}
                onValue={setCredit}
                id={id}
                placeholder={"Credit"}
            />
            <Button type="button" size={"sm"} onClick={onSubmit}>
                Save
            </Button>
        </div>
    );
};
